import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLogo } from '../context/LogoContext';
import { login as loginApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, Shield, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await loginApi(email, password);
      login(response.data.access_token, response.data.user);
      
      // Redirect based on role
      const role = response.data.user.role;
      if (role === 'municipal_admin') {
        navigate('/municipal');
      } else if (role === 'barangay_admin') {
        navigate('/barangay');
      } else if (role === 'bhw') {
        navigate('/bhw');
      } else if (role === 'emt') {
        navigate('/emt');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 text-white">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Organization logo" 
                className="max-h-12 max-w-[180px] object-contain brightness-0 invert"
                data-testid="login-logo"
              />
            ) : (
              <>
                <Shield className="h-10 w-10" />
                <div>
                  <h1 className="text-2xl font-bold font-['Public_Sans']">Municipal Health</h1>
                  <p className="text-primary-foreground/80 text-sm">& Resilience Operations</p>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="text-white">
          <h2 className="text-4xl font-bold font-['Public_Sans'] leading-tight mb-4">
            Empowering Communities Through Digital Health Solutions
          </h2>
          <p className="text-primary-foreground/80 text-lg">
            A comprehensive platform for barangay health management, emergency response, and community services.
          </p>
        </div>
        
        <div className="flex items-center gap-4 text-white/70 text-sm">
          <Building2 className="h-5 w-5" />
          <span>Municipality of San Isidro</span>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-2 text-primary mb-4">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Organization logo" 
                  className="max-h-10 max-w-[150px] object-contain"
                  data-testid="login-logo-mobile"
                />
              ) : (
                <>
                  <Shield className="h-8 w-8" />
                  <span className="font-bold text-xl font-['Public_Sans']">Municipal Health</span>
                </>
              )}
            </div>
            <CardTitle className="text-2xl font-bold font-['Public_Sans']">Welcome back</CardTitle>
            <CardDescription>
              Sign in to access the health operations platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/20">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-muted-foreground text-center mb-3">Demo Accounts</p>
              <div className="grid gap-2 text-xs text-muted-foreground">
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>Municipal Admin</span>
                  <span className="font-mono">admin@sanisidro.gov.ph</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>Barangay Admin</span>
                  <span className="font-mono">mabini@sanisidro.gov.ph</span>
                </div>
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>BHW</span>
                  <span className="font-mono">bhw1@sanisidro.gov.ph</span>
                </div>
                <p className="text-center mt-2 text-muted-foreground/70">Password: admin123 / bhw123</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
